import { apiService } from './api';
import { LoginRequest, LoginResponse, User, ApiResponse } from '../types';

class AuthService {
  private readonly ADMIN_TOKEN_KEY = 'admin_token';
  private readonly ADMIN_USER_KEY = 'admin_user';

  /**
   * Login admin user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: any = await apiService.post('/api/auth/admin/login', credentials);
      
      if (response.success && response.payload) {
        // Verify user has ADMIN role
        if (response.payload.role !== 'ADMIN') {
          throw new Error('Access denied. Admin privileges required.');
        }

        // Map backend response to frontend expected structure
        const user: User = {
          id: response.payload.userId,
          email: response.payload.email,
          firstName: response.payload.firstName,
          lastName: response.payload.lastName,
          phoneNumber: undefined,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userRole: {
            id: 1,
            roleName: 'ADMIN'
          },
          permissions: response.payload.permissions || []
        };

        const loginResponse: LoginResponse = {
          token: response.payload.token,
          user: user,
          expiresIn: 86400 // 24 hours default
        };

        // Store token and user data
        this.setAuthData(loginResponse.token, loginResponse.user);
        
        return loginResponse;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed. Please check your credentials.');
    }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint if available
      await apiService.post('/api/auth/admin/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local data
      this.clearAuthData();
    }
  }

  /**
   * Verify current token and get user data
   */
  async verifyToken(): Promise<User | null> {
    try {
      const token = this.getToken();
      if (!token) {
        return null;
      }

      const response: any = await apiService.get('/api/auth/admin/verify');
      
      if (response.success && response.payload) {
        // Verify user still has ADMIN role
        if (response.payload.role !== 'ADMIN') {
          console.warn('Admin privileges revoked, clearing auth data');
          this.clearAuthData();
          throw new Error('Admin privileges revoked');
        }

        // Map backend response to frontend expected structure
        const user: User = {
          id: response.payload.id,
          email: response.payload.email,
          firstName: response.payload.firstName,
          lastName: response.payload.lastName,
          phoneNumber: response.payload.phone,
          isActive: true,
          createdAt: response.payload.createdAt,
          updatedAt: response.payload.updatedAt,
          userRole: {
            id: 1,
            roleName: response.payload.role
          },
          // verify gira a ogni bootstrap: una revoca di permessi si riflette
          // al primo refresh della pagina.
          permissions: response.payload.permissions || []
        };

        // Update stored user data
        this.setUser(user);
        return user;
      } else {
        console.warn('Token verification failed: invalid response, clearing auth data');
        this.clearAuthData();
        return null;
      }
    } catch (error: any) {
      console.error('Token verification error:', error);
      
      // Clear auth data on 401/403 (expired or unauthorized token)
      // For other errors (network, 500, etc.), keep the auth data
      if (error.code === '401' || error.code === '403' || error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Token is invalid or expired, clearing auth data');
        this.clearAuthData();
      } else {
        console.warn('Network or server error during token verification, keeping auth data:', error.message);
      }

      return null;
    }
  }

  /**
   * Get current user data from localStorage
   */
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem(this.ADMIN_USER_KEY);
      if (userData) {
        const user = JSON.parse(userData);
        // Verify user has ADMIN role
        if (user.userRole?.roleName === 'ADMIN') {
          return user;
        } else {
          this.clearAuthData();
          return null;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      this.clearAuthData();
      return null;
    }
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return localStorage.getItem(this.ADMIN_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated and has admin role
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user && user.userRole?.roleName === 'ADMIN');
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.userRole?.roleName === 'ADMIN' || false;
  }

  /**
   * Set authentication data
   */
  private setAuthData(token: string, user: User): void {
    localStorage.setItem(this.ADMIN_TOKEN_KEY, token);
    localStorage.setItem(this.ADMIN_USER_KEY, JSON.stringify(user));
    apiService.setAuthToken(token);
  }

  /**
   * Set user data
   */
  private setUser(user: User): void {
    localStorage.setItem(this.ADMIN_USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.ADMIN_TOKEN_KEY);
    localStorage.removeItem(this.ADMIN_USER_KEY);
    apiService.clearAuthToken();
  }

  /**
   * Refresh token if needed
   * Note: Backend doesn't implement refresh endpoint, so we clear auth data
   */
  async refreshToken(): Promise<boolean> {
    try {
      // Backend doesn't implement refresh endpoint
      // Clear auth data and require re-login
      console.warn('Token refresh not supported by backend, clearing auth data');
      this.clearAuthData();
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Initialize auth service - verify token on app start
   */
  async initialize(): Promise<User | null> {
    const token = this.getToken();
    if (token) {
      try {
        const user = await this.verifyToken();
        if (user) {
          console.log('Auth initialization successful');
        } else {
          console.warn('Auth initialization failed: no user returned');
        }
        return user;
      } catch (error: any) {
        console.warn('Auth initialization failed:', error);
        
        // Clear auth data on 401/403 (expired or unauthorized token)
        // For other errors (network, 500, etc.), keep the auth data and return current user
        if (error.code === '401' || error.code === '403' || error.response?.status === 401 || error.response?.status === 403) {
          console.warn('Token is invalid or expired during initialization, clearing auth data');
          this.clearAuthData();
          return null;
        } else {
          console.warn('Network or server error during initialization, using cached user data');
          return this.getCurrentUser();
        }
      }
    }
    return null;
  }
}

export const authService = new AuthService();
export default authService;